import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppDataProvider } from './context/AppDataContext';
import ErrorBoundary from './components/ErrorBoundary';
import { GlobalLoader } from './components/Loader';
import Toaster from './components/Toaster';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';
import PortalLayout from './components/layout/PortalLayout';
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import AuditorRoster from './pages/AuditorRoster/AuditorRoster';
import ClientsHub from './pages/ClientsHub/ClientsHub';
import ClientsRoute from './components/ClientsRoute';
import Audits from './pages/Audits/Audits';
import AuditPortal from './pages/AuditPortal/AuditPortal';
import AuditSetupWizard from './pages/AuditSetup/AuditSetupWizard';
import Devices from './pages/Devices/Devices';
import DevicesRoute from './components/DevicesRoute';
import ReportsHome from './pages/Reports/ReportsHome';
import UsersRoute from './components/UsersRoute';
import ManagementRoute from './components/ManagementRoute';
import StaffRoute from './components/StaffRoute';
import PortalRoute from './components/PortalRoute';
import HomeRedirect from './components/HomeRedirect';
import ClientAudits from './pages/ClientAudits/ClientAudits';
import './App.css';

function AuthScopedData({ children }) {
  const { token } = useAuth();

  return <AppDataProvider key={token || 'guest'}>{children}</AppDataProvider>;
}

function App() {
  return (
    <ErrorBoundary>
      <GlobalLoader />
      <Toaster />
      <AuthProvider>
        <AuthScopedData>
          <BrowserRouter>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              element={
                <ProtectedRoute>
                  <PortalLayout />
                </ProtectedRoute>
              }
            >
              <Route
                path="/audits/:auditId/portal"
                element={
                  <PortalRoute>
                    <AuditPortal />
                  </PortalRoute>
                }
              />
              <Route path="/client" element={<ClientAudits />} />
            </Route>
            <Route
              element={
                <ProtectedRoute>
                  <StaffRoute>
                    <AppLayout />
                  </StaffRoute>
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route
                path="/roster"
                element={
                  <UsersRoute>
                    <AuditorRoster />
                  </UsersRoute>
                }
              />
              <Route path="/users" element={<Navigate to="/roster?tab=users" replace />} />
              <Route path="/teams" element={<Navigate to="/roster?tab=teams" replace />} />
              <Route
                path="/clients"
                element={
                  <ClientsRoute>
                    <ClientsHub />
                  </ClientsRoute>
                }
              />
              <Route
                path="/organizations"
                element={<Navigate to="/clients?tab=clients" replace />}
              />
              <Route
                path="/outlets"
                element={<Navigate to="/clients?tab=stores" replace />}
              />
              <Route
                path="/audits"
                element={
                  <ManagementRoute>
                    <Audits />
                  </ManagementRoute>
                }
              />
              <Route
                path="/audits/:auditId/setup"
                element={
                  <ManagementRoute>
                    <AuditSetupWizard />
                  </ManagementRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ManagementRoute>
                    <ReportsHome />
                  </ManagementRoute>
                }
              />
              <Route path="/reports/:auditId" element={<Navigate to="/reports" replace />} />
              <Route
                path="/devices"
                element={
                  <DevicesRoute>
                    <Devices />
                  </DevicesRoute>
                }
              />
            </Route>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="*" element={<HomeRedirect />} />
            </Routes>
          </BrowserRouter>
        </AuthScopedData>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
